import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatCurrency, formatQty, unitLabel, UNIT_OPTIONS, convertWeight, downloadAuthedFile, API } from "../lib/api";
import { Plus, Trash2, Edit, X, Upload, Package, PackagePlus, ArrowUp, ArrowDown, ArrowLeftRight, Building2, Store, QrCode, Printer, RotateCw, Globe, FileUp, CheckCircle2, AlertTriangle, Video, Tag, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import ResponsiveTable from "../components/ResponsiveTable";
import PrintLabelModal from "../components/PrintLabelModal";

const STATUSES = ["Available", "Reserved", "Sold"];
const WAREHOUSES = [
  { value: "ONLINE_SALE", label: "Online Sale", short: "Online" },
  { value: "SHOWROOM", label: "Showroom Display", short: "Showroom" },
  { value: "LIVE_PRODUCT", label: "Live Product", short: "Live" },
];
const WAREHOUSE_LABEL = {
  ONLINE_SALE: "Online Sale",
  SHOWROOM: "Showroom",
  LIVE_PRODUCT: "Live Product",
};

// Common gem cut shapes — covers ~95% of fine jewelry inventory.
const GEM_SHAPES = [
  "Round",
  "Oval",
  "Pear",
  "Marquise",
  "Princess",
  "Cushion",
  "Emerald",
  "Asscher",
  "Radiant",
  "Heart",
  "Trillion",
  "Baguette",
  "Cabochon",
  "Briolette",
  "Rough",
  "Other",
];

// Lightweight client-side category inference — mirrors backend logic for
// instant feedback as the user types. Backend remains source of truth on save.
const DEFAULT_KEYWORDS = new Set([
  "ruby", "sapphire", "emerald", "diamond", "pearl", "tanzanite", "spinel",
  "topaz", "tourmaline", "garnet", "opal", "amethyst", "citrine",
  "aquamarine", "peridot", "jade", "alexandrite", "lapis", "turquoise",
  "moonstone", "onyx", "agate", "coral", "amber", "padparadscha",
  "jewelry", "ring", "necklace", "bracelet", "earrings", "earring",
  "pendant", "brooch", "anklet", "tiara", "cufflinks",
]);
const DEFAULT_SKIPS = new Set([
  "blue", "red", "pink", "yellow", "green", "white", "black", "purple",
  "violet", "orange", "brown", "grey", "gray", "golden", "rose", "royal",
  "vivid", "star", "deep", "light", "dark", "natural", "premium",
  "untreated", "heated", "pigeon", "intense", "vintage",
  "antique", "rare", "fine", "the", "and", "of", "with",
]);
function inferCategoryClient(name) {
  if (!name || !name.trim()) return "";
  const tokens = name
    .split(/\s+/)
    .map((t) => t.replace(/[.,;:!?()[\]"']/g, "").toLowerCase())
    .filter(Boolean);
  for (const t of tokens) if (DEFAULT_KEYWORDS.has(t)) return t.toUpperCase();
  for (const t of tokens) {
    if (DEFAULT_SKIPS.has(t)) continue;
    if (t && /^[a-z]/.test(t)) return t.toUpperCase();
  }
  return tokens[0]?.toUpperCase() || "";
}

const EMPTY = {
  name: "",
  sku: "",  // empty = auto-generate on backend (next_sku → "OJ-00xxx")
  category: "",
  category_locked: false,
  product_type: "",
  product_type_custom: "",
  metal: "",
  metal_custom: "",
  description: "",
  warehouse: "ONLINE_SALE",
  status: "Available",
  cost_price: 0,
  selling_price: 0,
  // `quantity` is the per-piece WEIGHT (public — surfaced on QR scan page).
  // `stock` is the inventory amount (admin-only, decremented by sales).
  quantity: 1,
  stock: 1,
  stock_unit: "pcs",
  stock_custom_unit: "",
  low_stock_threshold: 1,
  unit: "ct",
  custom_unit: "",
  photos: [],
  carat: "",
  color: "",
  shape: "",
  clarity: "",
  dimension: "",
  origin: "",
  certification_lab: "",
  cert_number: "",
  treatment: "",
  size: "",
  supplier_id: "",
  variants: [],
};

const PRODUCT_TYPES = [
  { value: "", label: "— None —" },
  { value: "bracelet", label: "Bracelet" },
  { value: "ring", label: "Ring" },
  { value: "pendant", label: "Pendant" },
  { value: "earring", label: "Earring" },
  { value: "loose_stones", label: "Loose Stones" },
  { value: "custom", label: "Custom…" },
];

const METALS = [
  { value: "", label: "— None —" },
  { value: "silver_925", label: "Silver 925" },
  { value: "gold_14k", label: "Gold 14K" },
  { value: "gold_18k", label: "Gold 18K" },
  { value: "copper", label: "Copper" },
  { value: "metal", label: "Metal" },
  { value: "custom", label: "Custom…" },
];

// Resolve a product's stored slug → display label (e.g. "gold_18k" → "Gold 18K").
// Falls back to the user-typed custom value when slug == "custom".
function labelFor(opts, slug, customVal) {
  if (!slug) return "";
  if (slug === "custom") return (customVal || "").trim();
  const hit = opts.find((o) => o.value === slug);
  return hit ? hit.label : slug;
}
export function productTypeLabel(p) {
  return labelFor(PRODUCT_TYPES, p?.product_type, p?.product_type_custom);
}
export function metalLabel(p) {
  return labelFor(METALS, p?.metal, p?.metal_custom);
}

export default function Inventory() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "staff";
  const canDelete = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  // Operator's home warehouse — used as the default for *new* product
  // creation. Sales users are server-side pinned to this warehouse so the
  // inventory list also stays scoped (they can't even see pieces sitting
  // in the other warehouse). Admin / staff / accountant see both.
  const userDefaultWarehouse = user?.default_warehouse || "ONLINE_SALE";
  const accessibleWarehouses = user?.accessible_warehouses || ["ONLINE_SALE", "SHOWROOM"];
  // A sales user pinned to a single warehouse → hide the warehouse filter
  // dropdown entirely (only one option would show anyway) and tag the page.
  const isWarehousePinned = accessibleWarehouses.length === 1;

  const [products, setProducts] = useState([]);
  const [needsPricingCount, setNeedsPricingCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [needsPricing, setNeedsPricing] = useState(false);  // chip: selling_price ≤ 0
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]); // existing categories with counts
  const [movements, setMovements] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(null);
  const [showBulkStock, setShowBulkStock] = useState(false);
  const [showCategorySettings, setShowCategorySettings] = useState(false);
  const [qrProduct, setQrProduct] = useState(null);
  const fileRef = useRef(null);

  // Multi-select for "Print Selected Labels" bulk QR action.
  // Set<product_id>. Cleared on filter change to avoid hidden-selection bugs.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(products.map((p) => p.id)));

  // Nimbot / thermal label printer modal state — single-product OR batch.
  // `labelProducts` = [] closed, [one] single, [many] batch. Same modal.
  const [labelProducts, setLabelProducts] = useState([]);

  // Deep-link: ?edit={product_id} opens the edit modal directly. Used from
  // the supplier-detail page (CRM) so the operator can jump from "pieces
  // supplied" straight into editing a piece without rediscovering it in the
  // Inventory list.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/products/${editId}`);
        if (cancelled) return;
        openEdit(data);
      } catch {
        toast.error("Product not found");
      } finally {
        // Drop the param so a back-nav or refresh doesn't reopen the modal.
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set("status", filterStatus);
    if (filterWarehouse) params.set("warehouse", filterWarehouse);
    if (search) params.set("search", search);
    const [{ data: prods }, catsRes] = await Promise.all([
      api.get(`/products?${params}`),
      api.get("/products/categories").catch(() => ({ data: [] })),
    ]);
    let visible = prods;
    if (filterCategory) {
      visible = visible.filter((p) => (p.category || "").toUpperCase() === filterCategory.toUpperCase());
    }
    // Count unpriced products in the CURRENT scope (status/warehouse/search/
    // category filters all respected) BEFORE the needsPricing filter is
    // applied — so the chip count reflects "what's about to get filtered to".
    const unpricedCount = visible.filter((p) => !p.selling_price || Number(p.selling_price) <= 0).length;
    setNeedsPricingCount(unpricedCount);
    if (needsPricing) {
      visible = visible.filter((p) => !p.selling_price || Number(p.selling_price) <= 0);
    }
    setProducts(visible);
    setCategories(catsRes.data || []);
  };

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/crm/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
      api.get("/products/stock-movements/all").then(({ data }) => setMovements(data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [filterStatus, filterWarehouse, filterCategory, search, needsPricing]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, warehouse: userDefaultWarehouse || EMPTY.warehouse });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, carat: p.carat ?? "", variants: p.variants || [] });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Coerce numeric fields. Blank → 0 (intentional sentinel for "not in
    // stock yet"). NaN would bubble up as `null` and trigger an opaque 422
    // from the backend; we guard against that here.
    const num = (v, fallback = 0) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    };
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    if (!form.category?.trim()) { toast.error("Category is required"); return; }
    // SKU is optional — backend auto-generates `GV-00xxx` if left blank.
    try {
      const payload = {
        ...form,
        cost_price: num(form.cost_price, 0),
        selling_price: num(form.selling_price, 0),
        quantity: num(form.quantity, 0),
        stock: num(form.stock, num(form.quantity, 0)),
        stock_unit: form.stock_unit || "pcs",
        stock_custom_unit: form.stock_unit === "custom" ? (form.stock_custom_unit || "") : "",
        low_stock_threshold: num(form.low_stock_threshold, 0),
        carat: form.carat === "" || form.carat == null ? null : num(form.carat, null),
        supplier_id: form.supplier_id || null,
        unit: form.unit || "pcs",
        custom_unit: form.unit === "custom" ? (form.custom_unit || "") : "",
        variants: (form.variants || [])
          .filter((v) => (v.color || "").trim().length > 0)
          .map((v) => ({
            id: v.id || undefined,
            color: v.color.trim(),
            shape: (v.shape || "").trim(),
            size_mm: v.size_mm === "" || v.size_mm == null ? null : num(v.size_mm, null),
            size_label: (v.size_label || "").trim(),
            quantity: num(v.quantity, 0),
            stock: num(v.stock, num(v.quantity, 0)),
            stock_unit: v.stock_unit || "pcs",
            stock_custom_unit: v.stock_unit === "custom" ? (v.stock_custom_unit || "") : "",
            low_stock_threshold: num(v.low_stock_threshold, 0),
            selling_price: num(v.selling_price, 0),
            cost_price: num(v.cost_price, 0),
            sku_suffix: (v.sku_suffix || "").trim(),
          })),
      };
      if (editing) {
        await api.patch(`/products/${editing.id}`, payload);
        toast.success("Piece updated");
      } else {
        await api.post("/products", payload);
        toast.success("Piece added");
      }
      setOpen(false);
      load();
    } catch (err) {
      // FastAPI 422 returns `detail` as an array of validation errors. Render
      // them human-friendly instead of "[object Object]".
      const detail = err.response?.data?.detail;
      let msg = "Failed";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.map((d) => {
          const field = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
          return field ? `${field}: ${d.msg}` : d.msg;
        }).join(" · ");
      }
      toast.error(msg);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onExportToWebsite = async (p) => {
    if (p.exported_to_website) {
      toast.info("Already exported — manage on the Website page");
      return;
    }
    if (!window.confirm(`Export "${p.name}" to the website?\n\nA listing will be created (price + stock auto-sync from inventory). Media gallery starts empty — upload website-specific images on the Website page.`)) return;
    try {
      await api.post("/website/listings", { product_id: p.id });
      toast.success("Exported to website");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  // Gemstone warranty card — one-tap PDF download (A5 landscape, Certificate
  // of Authenticity + warranty terms + QR + logo + signature/stamp). Blank
  // fields on the product auto-hide server-side, so a loose stone with no
  // ring-size prints a clean card without a dangling row.
  const downloadWarrantyCard = async (p) => {
    try {
      const res = await api.get(`/products/${p.id}/warranty-card.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank", "noopener");
      // Best-effort: some mobile browsers block programmatic print, so we
      // just open the PDF in a new tab and let the user print/save.
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
      if (!w) {
        // Popup blocked — fall back to a direct download.
        const a = document.createElement("a");
        a.href = url;
        a.download = `warranty-${p.sku || p.id}.pdf`;
        a.click();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate warranty card");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photos: [...(f.photos || []), data.url] }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submitMovement = async (mvData) => {
    try {
      await api.post("/products/stock-movement", mvData);
      const label = mvData.type === "SET" ? `Stock set to ${mvData.quantity}` : `Stock ${mvData.type}: ${mvData.quantity}`;
      toast.success(label);
      setShowMoveModal(null);
      load();
      const { data: m } = await api.get("/products/stock-movements/all");
      setMovements(m);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length) {
        msg = detail.map((d) => d.msg).join(" · ");
      }
      toast.error(msg);
    }
  };

  // ----- Bulk QR (cashier-friendly) -----
  // Marketplace export — Shopee/Tokopedia/TikTok (xlsx) + Amazon/eBay/Etsy (csv)
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Default format per platform (matches backend `default_format()`).
  const MARKETPLACE_FORMATS = {
    shopee: "xlsx", tokopedia: "xlsx", tiktok: "xlsx",
    amazon: "csv", ebay: "csv", etsy: "csv",
  };
  const exportMarketplace = async (platform) => {
    setExportMenuOpen(false);
    if (exportBusy) return;
    // Prefer explicit selection; fall back to entire currently-visible list.
    const ids = selectedIds.size > 0
      ? Array.from(selectedIds)
      : products.map((p) => p.id);
    if (ids.length === 0) {
      toast.error("No products to export");
      return;
    }
    setExportBusy(true);
    try {
      const csv = encodeURIComponent(ids.join(","));
      const stamp = new Date().toISOString().slice(0, 10);
      const ext = MARKETPLACE_FORMATS[platform] || "xlsx";
      await downloadAuthedFile(
        `/products/marketplace-export?platform=${platform}&product_ids=${csv}`,
        `${platform}-export-${ids.length}items-${stamp}.${ext}`,
      );
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} ${ext} downloaded (${ids.length} products)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  const [bulkQrBusy, setBulkQrBusy] = useState(false);
  const printAllHangtags = async (idsArr = null) => {
    if (bulkQrBusy) return;
    const ids = Array.isArray(idsArr) && idsArr.length > 0 ? idsArr : null;
    const scopeLabel = ids
      ? `${ids.length} selected piece${ids.length === 1 ? "" : "s"}`
      : "every product missing one";
    if (!window.confirm(`Generate QR for ${scopeLabel}, then download a hangtag PDF?\n\nProducts with variants will produce one tag per variant (each with its own auto-generated SKU suffix, e.g. OJ-02340-A, OJ-02340-B).`)) return;
    setBulkQrBusy(true);
    try {
      const csv = ids ? ids.join(",") : "";
      const { data } = await api.post("/products/qr/enable-all" + (csv ? `?product_ids=${encodeURIComponent(csv)}` : ""));
      if (data?.enabled > 0) {
        toast.success(`QR enabled for ${data.enabled} new product${data.enabled === 1 ? "" : "s"}`);
      }
      const dlUrl = "/products/qr/bulk-hangtag.pdf?size=small" + (csv ? `&product_ids=${encodeURIComponent(csv)}` : "");
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = ids ? `hangtags-selected-${ids.length}-${stamp}.pdf` : `all-hangtags-${stamp}.pdf`;
      await downloadAuthedFile(dlUrl, fname);
      toast.success("Hangtag PDF downloaded");
      if (ids) clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk hangtag failed");
    } finally {
      setBulkQrBusy(false);
    }
  };

  return (
    <Layout
      title="Inventory"
      subtitle="Atelier register"
      action={
        <div className="flex items-center gap-2">
          {/* Cashier-friendly: any logged-in role can generate QR + download
              hangtag PDF for the whole catalog in one tap. */}
          <button
            data-testid="bulk-qr-all-btn"
            onClick={printAllHangtags}
            disabled={bulkQrBusy}
            className="btn-outline-gold px-4 py-3 inline-flex items-center gap-2 disabled:opacity-50"
            title="Generate QR for every product and download a printable hangtag PDF"
          >
            <Printer className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="hidden sm:inline">{bulkQrBusy ? "Preparing…" : "Print All QR"}</span>
          </button>
          {canEdit && isAdmin && (
            <button
              data-testid="bulk-stock-btn"
              onClick={() => setShowBulkStock(true)}
              className="btn-outline-gold px-4 py-3 inline-flex items-center gap-2"
              title="Bulk update stock from a CSV (admin only)"
            >
              <FileUp className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Bulk Stock</span>
            </button>
          )}
          {canEdit && (
            <button
              data-testid="add-product-btn"
              onClick={openCreate}
              className="btn-gold px-5 py-3 inline-flex items-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
              Add Piece
            </button>
          )}
        </div>
      }
    >
      <Toaster theme="dark" position="top-right" />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          data-testid="inventory-search"
          placeholder="Search name, SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="luxe-input px-4 py-2.5 text-sm w-full sm:w-64"
        />
        <select
          data-testid="filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="luxe-input px-4 py-2.5 text-sm"
        >
          <option value="">All Status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* Warehouse filter — hidden for pinned sales users (single-warehouse). */}
        {!isWarehousePinned && (
          <select
            data-testid="filter-warehouse"
            value={filterWarehouse}
            onChange={(e) => setFilterWarehouse(e.target.value)}
            className="luxe-input px-4 py-2.5 text-sm"
          >
            <option value="">All Warehouses</option>
            {WAREHOUSES.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        )}
        {isWarehousePinned && (
          <div
            className="luxe-input px-4 py-2.5 text-xs mono uppercase tracking-wider text-[#D4AF37] border-[#D4AF37]/40"
            data-testid="warehouse-pinned-badge"
            title="Your account is pinned to this warehouse by the admin"
          >
            {WAREHOUSE_LABEL[accessibleWarehouses[0]] || "Online Sale"} only
          </div>
        )}
        {isAdmin && (
          <button
            onClick={() => setShowCategorySettings(true)}
            data-testid="open-category-settings"
            className="btn-outline-gold px-4 py-2.5 text-xs uppercase tracking-wider"
            title="Edit auto-category rules"
          >
            Category Rules
          </button>
        )}
      </div>

      {/* Category chips (auto-generated from existing data) */}
      {(categories.length > 0 || needsPricingCount > 0 || needsPricing) && (
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="category-chips">
          {/* "Needs Pricing" alert chip — pinned first, amber when there is
              unpriced stock to flag. Cashier/admin can toggle to see only
              auto-synced or freshly added pieces that still have selling_price=0. */}
          {(needsPricingCount > 0 || needsPricing) && (
            <button
              onClick={() => setNeedsPricing((v) => !v)}
              data-testid="chip-needs-pricing"
              title="Show only products that still need a retail selling price"
              className={`px-3 py-1 text-[0.65rem] uppercase tracking-[0.16em] border transition-all inline-flex items-center gap-1.5 ${
                needsPricing
                  ? "border-amber-400 text-amber-300 bg-amber-400/10"
                  : "border-amber-400/30 text-amber-400/80 hover:border-amber-400/60 hover:text-amber-300"
              }`}
            >
              <AlertTriangle className="w-3 h-3" strokeWidth={1.75} />
              Needs Pricing
              <span className="mono ml-1 text-amber-300/90">{needsPricingCount}</span>
            </button>
          )}
          <button
            onClick={() => setFilterCategory("")}
            className={`px-3 py-1 text-[0.65rem] uppercase tracking-[0.16em] border transition-all ${
              !filterCategory ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#8A8A8A] hover:border-white/20"
            }`}
            data-testid="chip-all"
          >
            All ({products.length})
          </button>
          {categories.map((c) => {
            const active = filterCategory.toUpperCase() === c.name.toUpperCase();
            return (
              <button
                key={c.name}
                onClick={() => setFilterCategory(active ? "" : c.name)}
                data-testid={`chip-${c.name.toLowerCase()}`}
                className={`px-3 py-1 text-[0.65rem] uppercase tracking-[0.16em] border transition-all ${
                  active ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#8A8A8A] hover:border-white/20"
                }`}
              >
                {c.name} <span className="mono ml-1 text-[#666]">{c.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Table */}
      <ProductsTable
        products={products}
        isAdmin={isAdmin}
        canEdit={canEdit}
        canDelete={canDelete}
        onTransfer={setShowTransferModal}
        onMove={setShowMoveModal}
        onEdit={openEdit}
        onDelete={onDelete}
        onQr={setQrProduct}
        onExport={onExportToWebsite}
        onPrintLabel={(p) => setLabelProducts([p])}
        onWarrantyCard={(p) => downloadWarrantyCard(p)}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onSelectAllVisible={selectAllVisible}
        onClearSelection={clearSelection}
      />

      {/* Floating action bar — appears when 1+ pieces are checked. */}
      {selectedIds.size > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6 z-40 bg-[#0F0F0F] border border-[#D4AF37]/40 shadow-2xl px-4 py-3 flex items-center gap-3 max-w-[95vw]"
          data-testid="bulk-action-bar"
        >
          <div className="text-xs text-[#EAEAEA]">
            <span className="mono text-[#D4AF37] mr-1">{selectedIds.size}</span>
            selected
          </div>
          {selectedIds.size < products.length && (
            <button
              onClick={selectAllVisible}
              className="text-[0.65rem] mono uppercase tracking-wider text-[#C99356] hover:text-[#D4AF37] px-2 py-1"
              data-testid="bulk-select-all-visible"
              title={`Select all ${products.length} visible`}
            >
              + All visible ({products.length})
            </button>
          )}
          <button
            onClick={clearSelection}
            className="text-[0.65rem] mono uppercase tracking-wider text-[#8A8A8A] hover:text-[#EAEAEA] px-2 py-1"
            data-testid="bulk-clear"
          >
            Clear
          </button>
          <div className="w-px h-6 bg-white/10" />
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={exportBusy}
              className="btn-outline-gold px-4 py-2 inline-flex items-center gap-2 text-xs disabled:opacity-50"
              data-testid="bulk-marketplace-export"
              title="Export selected as Shopee / Tokopedia / TikTok / Amazon / eBay / Etsy bulk-upload file"
            >
              <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
              {exportBusy ? "Exporting…" : "Marketplace ▾"}
            </button>
            {exportMenuOpen && (
              <div
                className="absolute bottom-full mb-2 right-0 bg-[#0F0F0F] border border-[#D4AF37]/30 shadow-2xl w-56 z-50"
                data-testid="marketplace-menu"
              >
                <div className="px-4 py-2 text-[0.55rem] mono uppercase tracking-wider text-[#D4AF37] border-b border-white/10">
                  Indonesia
                </div>
                {[
                  { key: "shopee",    label: "Shopee",      ext: "xlsx" },
                  { key: "tokopedia", label: "Tokopedia",   ext: "xlsx" },
                  { key: "tiktok",    label: "TikTok Shop", ext: "xlsx" },
                ].map((p) => (
                  <button
                    key={p.key}
                    onClick={() => exportMarketplace(p.key)}
                    className="w-full text-left px-4 py-3 text-xs text-[#EAEAEA] hover:bg-[#D4AF37]/[0.08] border-b border-white/5"
                    data-testid={`export-${p.key}`}
                  >
                    {p.label} <span className="text-[0.6rem] text-[#666] mono ml-1">.{p.ext}</span>
                  </button>
                ))}
                <div className="px-4 py-2 text-[0.55rem] mono uppercase tracking-wider text-[#D4AF37] border-b border-white/10">
                  Global
                </div>
                {[
                  { key: "amazon", label: "Amazon",  ext: "csv" },
                  { key: "ebay",   label: "eBay",    ext: "csv" },
                  { key: "etsy",   label: "Etsy",    ext: "csv" },
                ].map((p) => (
                  <button
                    key={p.key}
                    onClick={() => exportMarketplace(p.key)}
                    className="w-full text-left px-4 py-3 text-xs text-[#EAEAEA] hover:bg-[#D4AF37]/[0.08] border-b border-white/5 last:border-b-0"
                    data-testid={`export-${p.key}`}
                  >
                    {p.label} <span className="text-[0.6rem] text-[#666] mono ml-1">.{p.ext}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => printAllHangtags(Array.from(selectedIds))}
            disabled={bulkQrBusy}
            className="btn-outline-gold px-4 py-2 inline-flex items-center gap-2 text-xs disabled:opacity-50"
            data-testid="bulk-print-selected"
          >
            <Printer className="w-3.5 h-3.5" strokeWidth={1.5} />
            {bulkQrBusy ? "Preparing…" : `Hangtags (${selectedIds.size})`}
          </button>
          <button
            onClick={() => setLabelProducts(products.filter((p) => selectedIds.has(p.id)))}
            className="btn-gold px-4 py-2 inline-flex items-center gap-2 text-xs"
            data-testid="bulk-print-nimbot-labels"
            title="Print Nimbot / thermal labels for selected pieces"
          >
            <Tag className="w-3.5 h-3.5" strokeWidth={1.5} />
            Nimbot Labels ({selectedIds.size})
          </button>
        </div>
      )}

      {/* Nimbot / thermal label printer modal — single + batch */}
      {labelProducts.length > 0 && (
        <PrintLabelModal
          products={labelProducts}
          onClose={() => setLabelProducts([])}
        />
      )}

      {/* Stock movements log */}
      {(user?.role === "admin" || user?.role === "staff") && (
      <div className="mt-10">
        <div className="overline mb-2">Activity</div>
        <h2 className="serif text-2xl mb-6">Stock Movements</h2>
        <div className="glass-card p-6">
          {movements.length === 0 ? (
            <div className="text-sm text-[#8A8A8A]">No movements recorded.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {movements.slice(0, 20).map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 group">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {m.type === "IN" ? (
                      <ArrowDown className="w-3.5 h-3.5 text-[#7BA585] shrink-0" strokeWidth={1.25} />
                    ) : (
                      <ArrowUp className="w-3.5 h-3.5 text-[#C99356] shrink-0" strokeWidth={1.25} />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm truncate">{m.product_name}</div>
                      <div className="text-[0.7rem] text-[#666] mono truncate">{m.product_sku} · {m.user_name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className={`mono text-sm ${m.type === "IN" ? "text-[#7BA585]" : "text-[#C99356]"}`}>
                        {m.type === "IN" ? "+" : "-"}{formatQty(m.quantity, m.product_unit, m.product_custom_unit)}
                      </div>
                      <div className="text-[0.65rem] text-[#666] mono">{m.created_at?.slice(0, 10)}</div>
                    </div>
                    {isAdmin && (
                      <button
                        data-testid={`delete-movement-${m.id}`}
                        onClick={async () => {
                          if (!window.confirm("Hide this movement from the log? (Stock level is NOT affected.)")) return;
                          try {
                            await api.delete(`/products/stock-movements/${m.id}`);
                            toast.success("Movement removed");
                            const { data: refreshed } = await api.get("/products/stock-movements/all");
                            setMovements(refreshed);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Delete failed");
                          }
                        }}
                        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 inline-flex items-center justify-center"
                        aria-label="Delete movement entry"
                        title="Hide this entry from the log"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Add/Edit Modal */}
      {open && (
        <ProductModal
          form={form}
          setForm={setForm}
          editing={editing}
          suppliers={suppliers}
          isAdmin={isAdmin}
          categories={categories}
          onClose={() => setOpen(false)}
          onSubmit={submit}
          onUpload={onUpload}
          fileRef={fileRef}
        />
      )}

      {/* Stock movement modal */}
      {showMoveModal && (
        <StockMoveModal
          product={showMoveModal}
          isAdmin={isAdmin}
          onClose={() => setShowMoveModal(null)}
          onSubmit={submitMovement}
        />
      )}

      {/* Warehouse transfer modal */}
      {showTransferModal && (
        <TransferModal
          product={showTransferModal}
          onClose={() => setShowTransferModal(null)}
          onSaved={() => { setShowTransferModal(null); load(); }}
        />
      )}

      {/* Bulk Stock CSV upload (admin only) */}
      {showBulkStock && (
        <BulkStockModal
          onClose={() => setShowBulkStock(false)}
          onApplied={() => { setShowBulkStock(false); load(); }}
        />
      )}

      {/* Category rules modal (admin only) */}
      {showCategorySettings && (
        <CategorySettingsModal
          onClose={() => setShowCategorySettings(false)}
          onSaved={() => { setShowCategorySettings(false); load(); }}
        />
      )}

      {/* QR Code modal (admin/staff only) */}
      {qrProduct && (
        <QRModal
          product={qrProduct}
          onClose={() => setQrProduct(null)}
          onChange={() => { load(); }}
        />
      )}
    </Layout>
  );
}

function ProductsTable({ products, isAdmin, canEdit, canDelete, onTransfer, onMove, onEdit, onDelete, onQr, onExport, onPrintLabel, onWarrantyCard, selectedIds, onToggleSelect, onSelectAllVisible, onClearSelection }) {
  const warehousePill = (p) => {
    // Color coding per warehouse — Online (gold) / Showroom (green) / Live (bronze)
    const wh = p.warehouse || "ONLINE_SALE";
    const wpalette = {
      ONLINE_SALE:  "border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/[0.05]",
      SHOWROOM:     "border-[#7BA585]/40 text-[#7BA585] bg-[#7BA585]/[0.08]",
      LIVE_PRODUCT: "border-[#C99356]/40 text-[#C99356] bg-[#C99356]/[0.06]",
    };
    return (
      <span
        className={`px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border inline-block ${wpalette[wh] || wpalette.ONLINE_SALE}`}
        data-testid={`warehouse-pill-${p.sku}`}
      >
        {WAREHOUSE_LABEL[wh] || "Online Sale"}
      </span>
    );
  };

  const columns = [
    {
      key: "sku",
      label: "SKU",
      width: "120px",
      cell: (p) => <span className="mono text-xs text-[#D4AF37]" data-testid={`product-sku-${p.sku}`}>{p.sku}</span>,
    },
    {
      key: "name",
      label: "Piece",
      width: "26%",
      cell: (p) => {
        const pt = productTypeLabel(p);
        const mt = metalLabel(p);
        const trail = [pt, mt].filter(Boolean).join(" · ");
        return (
          <div className="flex items-start gap-2 min-w-0">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={selectedIds?.has(p.id) || false}
                onChange={() => onToggleSelect(p.id)}
                onClick={(e) => e.stopPropagation()}
                className="accent-[#D4AF37] cursor-pointer mt-1 shrink-0"
                data-testid={`select-row-${p.sku}`}
                aria-label={`Select ${p.name} for bulk label printing`}
                title="Select for bulk label print"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate">{p.name}</div>
              <div className="text-[0.7rem] text-[#666] mt-0.5">{p.category}</div>
              {trail && (
                <div className="text-[0.65rem] text-[#C99356] mt-1 truncate" data-testid={`product-trail-${p.sku}`}>
                  {trail}
                </div>
              )}
              {p.variants && p.variants.length > 0 && (
                <div className="text-[0.65rem] text-[#7BA585] mt-1 mono" data-testid={`variants-summary-${p.sku}`}>
                  {p.variants.length} variant{p.variants.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "specs",
      label: "Specs",
      cell: (p) => (
        <span className="text-xs text-[#8A8A8A]">
          {[
            p.carat ? `${p.carat}ct` : null,
            p.color,
            p.shape,
            p.clarity,
          ].filter(Boolean).join(" · ") || "—"}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      key: "warehouse",
      label: "Warehouse",
      width: "130px",
      cell: warehousePill,
    },
    {
      key: "status",
      label: "Status",
      width: "150px",
      cell: (p) => (
        <div className="flex flex-col gap-1 items-start">
          <span className={`status-badge status-${p.status?.toLowerCase()}`}>{p.status}</span>
          {p.exported_to_website && (
            <span
              className="px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.16em] border border-[#7BA585]/40 text-[#7BA585] bg-[#7BA585]/[0.08] inline-flex items-center gap-1"
              data-testid={`exported-badge-${p.sku}`}
              title="Exported to the website"
            >
              <Globe className="w-2.5 h-2.5" strokeWidth={1.75} />
              Exported
            </span>
          )}
        </div>
      ),
    },
    ...(isAdmin
      ? [{ key: "cost", label: "Cost", align: "right", width: "120px", cell: (p) => <span className="mono">{formatCurrency(p.cost_price)}</span>, hideOnMobile: true }]
      : []),
    {
      key: "retail",
      label: "Retail",
      align: "right",
      width: "130px",
      cell: (p) => <span className="mono text-[#D4AF37]">{formatCurrency(p.selling_price)}</span>,
    },
    {
      key: "stock",
      label: "Stock",
      align: "right",
      width: "110px",
      cell: (p) => {
        // Show stock with its own unit (admin-only); falls back to legacy quantity for un-migrated rows.
        const stockVal = p.stock != null ? p.stock : p.quantity;
        const stockUnit = p.stock_unit || p.unit || "pcs";
        const stockCustom = p.stock_custom_unit || p.custom_unit || "";
        return <span className="mono">{formatQty(stockVal, stockUnit, stockCustom)}</span>;
      },
    },
  ];

  const rowActions = (p) => (
    <>
      {canEdit && (
        <>
          {isAdmin && (
            <button
              data-testid={`export-website-${p.sku}`}
              onClick={() => onExport(p)}
              disabled={p.exported_to_website}
              className={`min-h-[44px] sm:min-h-0 p-2 transition-colors inline-flex items-center justify-center ${
                p.exported_to_website
                  ? "text-[#7BA585] cursor-default"
                  : "text-[#8A8A8A] hover:text-[#7BA585]"
              }`}
              title={p.exported_to_website ? "Already on the website" : "Export to website"}
              aria-label="Export to website"
            >
              <Globe className="w-4 h-4" strokeWidth={1.25} />
            </button>
          )}
          <button
            data-testid={`qr-${p.sku}`}
            onClick={() => onQr(p)}
            className={`min-h-[44px] sm:min-h-0 p-2 transition-colors inline-flex items-center justify-center ${
              p.qr_enabled ? "text-[#D4AF37]" : "text-[#8A8A8A] hover:text-[#D4AF37]"
            }`}
            title={p.qr_enabled ? `QR active · ${p.qr_short_id}` : "Generate QR label"}
            aria-label="QR Code"
          >
            <QrCode className="w-4 h-4" strokeWidth={1.25} />
          </button>
          <button
            data-testid={`print-label-${p.sku}`}
            onClick={() => onPrintLabel?.(p)}
            className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37] transition-colors inline-flex items-center justify-center"
            title="Print Nimbot / thermal label"
            aria-label="Print label"
          >
            <Tag className="w-4 h-4" strokeWidth={1.25} />
          </button>
          <button
            data-testid={`warranty-${p.sku}`}
            onClick={() => onWarrantyCard?.(p)}
            className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#7BA585] transition-colors inline-flex items-center justify-center"
            title="Download Gemstone Warranty Card"
            aria-label="Warranty card"
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={1.25} />
          </button>
          <button
            data-testid={`transfer-${p.sku}`}
            onClick={() => onTransfer(p)}
            className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#7BA585] transition-colors inline-flex items-center justify-center"
            title="Transfer between warehouses"
            aria-label="Transfer"
          >
            <ArrowLeftRight className="w-4 h-4" strokeWidth={1.25} />
          </button>
          <button
            data-testid={`stock-move-${p.sku}`}
            onClick={() => onMove(p)}
            className="min-h-[44px] sm:min-h-0 px-2 py-1.5 sm:p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors inline-flex sm:items-center sm:justify-center flex-col gap-0.5 border border-[#D4AF37]/30 sm:border-0"
            title="Add or remove stock"
            aria-label="Adjust stock"
          >
            <PackagePlus className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-[0.55rem] uppercase tracking-wider sm:hidden">+ Stock</span>
          </button>
          <button
            data-testid={`edit-${p.sku}`}
            onClick={() => onEdit(p)}
            className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37] transition-colors inline-flex items-center justify-center"
            aria-label="Edit"
          >
            <Edit className="w-4 h-4" strokeWidth={1.25} />
          </button>
        </>
      )}
      {canDelete && (
        <button
          data-testid={`delete-${p.sku}`}
          onClick={() => onDelete(p)}
          className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 transition-colors inline-flex items-center justify-center"
          aria-label="Delete"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.25} />
        </button>
      )}
    </>
  );

  return (
    <ResponsiveTable
      columns={columns}
      rows={products}
      rowKey={(p) => p.id}
      testid="products-table"
      rowActions={rowActions}
      emptyMessage="No pieces found."
      mobileCard={(p) => ({
        title: <span>{p.name}</span>,
        subtitle: <span>{p.sku} · {p.category}</span>,
        fields: [
          { label: "Stock", value: <span className="mono">{formatQty(p.stock != null ? p.stock : p.quantity, p.stock_unit || p.unit, p.stock_custom_unit || p.custom_unit)}</span> },
          { label: "Retail", value: <span className="mono text-[#D4AF37]">{formatCurrency(p.selling_price)}</span> },
          { label: "Warehouse", value: warehousePill(p) },
          { label: "Status", value: <span className={`status-badge status-${p.status?.toLowerCase()}`}>{p.status}</span> },
          ...(isAdmin ? [{ label: "Cost", value: <span className="mono">{formatCurrency(p.cost_price)}</span> }] : []),
          { label: "Specs", value: <span className="text-[#8A8A8A]">{[p.carat ? `${p.carat}ct` : null, p.color, p.shape, p.clarity].filter(Boolean).join(" · ") || "—"}</span> },
        ],
        actions: rowActions(p),
      })}
    />
  );
}

function ProductModal({ form, setForm, editing, suppliers, isAdmin, categories, onClose, onSubmit, onUpload, fileRef }) {
  const inferredFromName = inferCategoryClient(form.name);
  // Auto-fill category when name changes IF user hasn't locked it manually
  const onNameChange = (val) => {
    setForm((prev) => {
      const next = { ...prev, name: val };
      if (!prev.category_locked) {
        next.category = inferCategoryClient(val);
      }
      return next;
    });
  };
  const onCategoryChange = (val) => {
    setForm((prev) => ({
      ...prev,
      category: val.toUpperCase(),
      category_locked: val.trim().length > 0 && val.toUpperCase() !== inferCategoryClient(prev.name),
    }));
  };
  const resetCategoryToAuto = () => {
    setForm((prev) => ({
      ...prev,
      category: inferCategoryClient(prev.name),
      category_locked: false,
    }));
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto fade-up" data-testid="product-modal">
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-white/5 px-8 py-6 flex justify-between items-center">
          <div>
            <div className="overline">{editing ? "Edit Piece" : "New Piece"}</div>
            <h2 className="serif text-2xl mt-1">{editing ? editing.name : "Add to Atelier"}</h2>
          </div>
          <button onClick={onClose} data-testid="close-modal" className="text-[#8A8A8A] hover:text-[#EAEAEA]">
            <X className="w-5 h-5" strokeWidth={1.25} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="px-8 py-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Name" testid="form-name">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              required
              value={form.name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="e.g. Ruby Star 20.000"
            />
          </Field>
          <Field label="SKU (optional — auto-generated)" testid="form-sku">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm mono"
              value={form.sku || ""}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder={editing ? "—" : "Leave blank to auto-generate (e.g. OJ-00351)"}
              disabled={!!editing}
              data-testid="sku-input"
            />
            {editing && (
              <div className="text-[0.6rem] text-[#666] mt-1">SKU is fixed after creation to keep history intact.</div>
            )}
          </Field>
          <Field label="Category (auto from name)" testid="form-category">
            <div className="flex items-center gap-2">
              <input
                className="luxe-input w-full px-4 py-2.5 text-sm uppercase tracking-wider"
                value={form.category}
                list="gv-category-options"
                onChange={(e) => onCategoryChange(e.target.value)}
                placeholder={inferredFromName || "—"}
                data-testid="category-input"
              />
              {form.category_locked && (
                <button
                  type="button"
                  onClick={resetCategoryToAuto}
                  className="text-[0.62rem] uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37] whitespace-nowrap"
                  data-testid="reset-category-auto"
                  title="Reset to auto-detected category"
                >
                  Auto
                </button>
              )}
            </div>
            <datalist id="gv-category-options">
              {categories.map((c) => (
                <option key={c.name} value={c.name} />
              ))}
            </datalist>
            <div className="mt-1.5 text-[0.65rem] mono text-[#8A8A8A]" data-testid="category-hint">
              {form.category_locked ? (
                <span className="text-[#C99356]">Manual override · click "Auto" to revert</span>
              ) : (
                <span>Auto-detected: <span className="text-[#D4AF37]">{form.category || "—"}</span></span>
              )}
            </div>
          </Field>
          <Field label="Warehouse">
            <select className="luxe-input w-full px-4 py-2.5 text-sm" value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} data-testid="form-warehouse">
              {WAREHOUSES.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className="luxe-input w-full px-4 py-2.5 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          {isAdmin && (
            <Field label="Cost Price (IDR)">
              <input type="number" step="1" className="luxe-input w-full px-4 py-2.5 text-sm" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} disabled={!isAdmin} />
            </Field>
          )}
          <Field label="Selling Price (IDR)">
            <input type="number" step="1" className="luxe-input w-full px-4 py-2.5 text-sm" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
          </Field>
          <Field label="Measurement Unit">
            <select
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              data-testid="form-unit"
            >
              {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Field>
          {form.unit === "custom" ? (
            <Field label="Custom Unit Label">
              <input
                className="luxe-input w-full px-4 py-2.5 text-sm"
                value={form.custom_unit}
                onChange={(e) => setForm({ ...form, custom_unit: e.target.value })}
                placeholder="e.g. parcel, set, lot"
                data-testid="form-custom-unit"
              />
            </Field>
          ) : (
            <Field label="Low Stock Threshold">
              <input
                type="number" step="0.01"
                className="luxe-input w-full px-4 py-2.5 text-sm"
                value={form.low_stock_threshold}
                onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
              />
            </Field>
          )}
          <div className="md:col-span-2">
            <Field
              label={`Weight (per piece) (${unitLabel(form.unit, form.custom_unit)})`}
              hint="Surfaced publicly on the QR scan page. Enter the weight/spec of ONE single piece."
            >
              <input
                type="number" step="0.01" min="0"
                className="luxe-input w-full px-4 py-2.5 text-sm"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="form-quantity"
              />
              {/* live weight-unit conversion hint */}
              {["ct", "g", "kg"].includes(form.unit) && form.quantity !== "" && (
                <div className="mt-2 text-[0.7rem] text-[#8A8A8A] mono" data-testid="qty-conversion">
                  ≈
                  {form.unit !== "ct" && (
                    <span> {convertWeight(form.quantity, form.unit, "ct")?.toFixed(2)} ct</span>
                  )}
                  {form.unit !== "g" && (
                    <span> · {convertWeight(form.quantity, form.unit, "g")?.toFixed(2)} g</span>
                  )}
                  {form.unit !== "kg" && (
                    <span> · {convertWeight(form.quantity, form.unit, "kg")?.toFixed(4)} kg</span>
                  )}
                </div>
              )}
            </Field>
          </div>
          {/* Stock: independent inventory amount + own unit selector. NEVER surfaced publicly. */}
          <div className="md:col-span-2 border border-[#C99356]/20 bg-[#C99356]/5 p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="overline text-[#C99356]">Stock (admin only)</span>
              <span className="text-[0.65rem] text-[#8A8A8A]">Not shown publicly</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number" step="0.01" min="0"
                className="luxe-input w-full px-4 py-2.5 text-sm"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                placeholder="e.g. 3 or 12.52"
                data-testid="form-stock"
              />
              <select
                className="luxe-input w-full px-4 py-2.5 text-sm"
                value={form.stock_unit || "pcs"}
                onChange={(e) => setForm({ ...form, stock_unit: e.target.value })}
                data-testid="form-stock-unit"
              >
                <option value="pcs">pcs</option>
                <option value="ct">ct</option>
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="custom">custom…</option>
              </select>
            </div>
            {form.stock_unit === "custom" && (
              <input
                className="luxe-input w-full px-4 py-2.5 text-sm mt-2"
                value={form.stock_custom_unit || ""}
                onChange={(e) => setForm({ ...form, stock_custom_unit: e.target.value })}
                placeholder="e.g. parcels, sets, lots…"
                data-testid="form-stock-custom-unit"
              />
            )}
          </div>
          {form.unit === "custom" && (
            <Field label={`Low Stock Threshold (${unitLabel(form.unit, form.custom_unit)})`}>
              <input
                type="number" step="0.01"
                className="luxe-input w-full px-4 py-2.5 text-sm"
                value={form.low_stock_threshold}
                onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
              />
            </Field>
          )}
          <Field label="Carat (spec ref.)">
            <input type="number" step="0.01" className="luxe-input w-full px-4 py-2.5 text-sm" value={form.carat} onChange={(e) => setForm({ ...form, carat: e.target.value })} />
          </Field>
          <Field label="Color">
            <input className="luxe-input w-full px-4 py-2.5 text-sm" value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </Field>
          <Field label="Shape">
            <select
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.shape || ""}
              onChange={(e) => setForm({ ...form, shape: e.target.value })}
              data-testid="product-shape"
            >
              <option value="">— select shape —</option>
              {GEM_SHAPES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Clarity">
            <input className="luxe-input w-full px-4 py-2.5 text-sm" value={form.clarity || ""} onChange={(e) => setForm({ ...form, clarity: e.target.value })} />
          </Field>
          <Field label="Dimension">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.dimension || ""}
              onChange={(e) => setForm({ ...form, dimension: e.target.value })}
              placeholder="e.g. 8.5 × 6.2 × 4.1 mm"
              data-testid="form-dimension"
            />
          </Field>
          <Field label="Size">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.size || ""}
              onChange={(e) => setForm({ ...form, size: e.target.value })}
              placeholder="e.g. Ring 7, 18 in chain"
              data-testid="form-size"
            />
          </Field>
          <Field label="Origin">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.origin || ""}
              onChange={(e) => setForm({ ...form, origin: e.target.value })}
              placeholder="e.g. Burma (Mogok)"
              data-testid="form-origin"
            />
          </Field>
          <Field label="Certification Lab">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.certification_lab || ""}
              onChange={(e) => setForm({ ...form, certification_lab: e.target.value })}
              placeholder="e.g. GRS, GIA, SSEF"
              data-testid="form-certification-lab"
            />
          </Field>
          <Field label="Cert Number">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.cert_number || ""}
              onChange={(e) => setForm({ ...form, cert_number: e.target.value })}
              placeholder="e.g. GRS-2024-12345"
              data-testid="form-cert-number"
            />
          </Field>
          <Field label="Treatment">
            <input
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.treatment || ""}
              onChange={(e) => setForm({ ...form, treatment: e.target.value })}
              placeholder="e.g. Heated, No Heat, Oiled"
              data-testid="form-treatment"
            />
          </Field>
          {/* ---- Variants editor (color × size × qty) ---- */}
          <div className="md:col-span-2 border border-[#D4AF37]/15 p-5 bg-[#D4AF37]/[0.02]">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline text-[#D4AF37]">Variants (optional)</div>
                <div className="text-[0.65rem] text-[#666] mt-1">
                  Each variant has its own Weight (per piece — public spec) and Stock (admin-only inventory count). Parent's Stock auto-sums from variants.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, variants: [...(form.variants || []), { color: "", shape: "", size_mm: "", quantity: 0, stock: 1, stock_unit: "pcs", stock_custom_unit: "", selling_price: 0, cost_price: 0 }] })}
                className="btn-outline-gold px-3 py-1.5 text-[0.65rem] uppercase tracking-wider"
                data-testid="add-variant"
              >
                + Add variant
              </button>
            </div>
            {(form.variants || []).length === 0 ? (
              <div className="text-[0.7rem] text-[#666] italic">No variants — this piece is a single inventory line.</div>
            ) : (
              <div className="space-y-3">
                {(form.variants || []).map((v, i) => {
                  const update = (patch) => {
                    const next = [...form.variants];
                    next[i] = { ...v, ...patch };
                    setForm({ ...form, variants: next });
                  };
                  return (
                  <div key={v.id || `new-${i}`} className="border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid={`variant-row-${i}`}>
                    {/* Row 1: identity */}
                    <div className="grid grid-cols-12 gap-2">
                      <input
                        placeholder="Color"
                        value={v.color || ""}
                        onChange={(e) => update({ color: e.target.value })}
                        className="luxe-input col-span-3 px-3 py-2 text-sm"
                        data-testid={`variant-color-${i}`}
                      />
                      <input
                        placeholder="Shape"
                        value={v.shape || ""}
                        onChange={(e) => update({ shape: e.target.value })}
                        className="luxe-input col-span-3 px-3 py-2 text-sm"
                        data-testid={`variant-shape-${i}`}
                      />
                      <input
                        type="number" step="0.1" placeholder="mm"
                        value={v.size_mm ?? ""}
                        onChange={(e) => update({ size_mm: e.target.value })}
                        className="luxe-input col-span-2 px-3 py-2 text-sm"
                        data-testid={`variant-size-${i}`}
                      />
                      <input
                        placeholder="Size label"
                        value={v.size_label || ""}
                        onChange={(e) => update({ size_label: e.target.value })}
                        className="luxe-input col-span-3 px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, variants: form.variants.filter((_, j) => j !== i) })}
                        className="col-span-1 text-[0.65rem] uppercase tracking-wider text-[#C99356] hover:text-red-400"
                        data-testid={`variant-remove-${i}`}
                      >
                        ✕
                      </button>
                    </div>
                    {/* Row 2: weight (public) + stock (admin) + unit */}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <div className="text-[0.55rem] uppercase tracking-wider text-[#8A8A8A] mb-1">Weight (per piece)</div>
                        <input
                          type="number" step="0.01" min="0"
                          placeholder="e.g. 18.39"
                          value={v.quantity ?? 0}
                          onChange={(e) => update({ quantity: e.target.value })}
                          className="luxe-input w-full px-3 py-2 text-sm mono"
                          data-testid={`variant-qty-${i}`}
                        />
                      </div>
                      <div className="col-span-4">
                        <div className="text-[0.55rem] uppercase tracking-wider text-[#C99356] mb-1">Stock (internal)</div>
                        <input
                          type="number" step="0.01" min="0"
                          placeholder="e.g. 3"
                          value={v.stock ?? 1}
                          onChange={(e) => update({ stock: e.target.value })}
                          className="luxe-input w-full px-3 py-2 text-sm mono"
                          data-testid={`variant-stock-${i}`}
                        />
                      </div>
                      <div className="col-span-4">
                        <div className="text-[0.55rem] uppercase tracking-wider text-[#C99356] mb-1">Unit</div>
                        <select
                          value={v.stock_unit || "pcs"}
                          onChange={(e) => update({ stock_unit: e.target.value })}
                          className="luxe-input w-full px-2 py-2 text-sm"
                          data-testid={`variant-stock-unit-${i}`}
                        >
                          <option value="pcs">pcs</option>
                          <option value="ct">ct</option>
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                          <option value="custom">custom</option>
                        </select>
                      </div>
                    </div>
                    {/* Row 3: per-variant cost (admin) + per-variant price */}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      {isAdmin && (
                        <div className="col-span-6">
                          <div className="text-[0.55rem] uppercase tracking-wider text-[#C99356] mb-1">Cost (IDR) <span className="text-[#666] normal-case tracking-normal">· per piece, blank = parent</span></div>
                          <input
                            type="number" step="1" min="0"
                            placeholder={`Default: ${Number(form.cost_price || 0).toLocaleString("id-ID")}`}
                            value={v.cost_price ?? 0}
                            onChange={(e) => update({ cost_price: e.target.value })}
                            className="luxe-input w-full px-3 py-2 text-sm mono"
                            data-testid={`variant-cost-${i}`}
                          />
                        </div>
                      )}
                      <div className={isAdmin ? "col-span-6" : "col-span-12"}>
                        <div className="text-[0.55rem] uppercase tracking-wider text-[#8A8A8A] mb-1">Price (IDR) <span className="text-[#666] normal-case tracking-normal">· per piece, blank = parent</span></div>
                        <input
                          type="number" step="1" min="0"
                          placeholder={`Default: ${Number(form.selling_price || 0).toLocaleString("id-ID")}`}
                          value={v.selling_price ?? 0}
                          onChange={(e) => update({ selling_price: e.target.value })}
                          className="luxe-input w-full px-3 py-2 text-sm mono"
                          data-testid={`variant-price-${i}`}
                        />
                      </div>
                    </div>
                    {v.stock_unit === "custom" && (
                      <input
                        placeholder="Custom unit (e.g. parcels)"
                        value={v.stock_custom_unit || ""}
                        onChange={(e) => update({ stock_custom_unit: e.target.value })}
                        className="luxe-input w-full px-3 py-2 text-sm"
                        data-testid={`variant-stock-custom-unit-${i}`}
                      />
                    )}
                  </div>
                  );
                })}
                <div className="mt-2 text-[0.7rem] mono text-[#D4AF37]" data-testid="variants-total">
                  Total stock (auto-summed): {(form.variants || []).reduce((a, v) => a + (parseFloat(v.stock ?? v.quantity) || 0), 0)}
                </div>
              </div>
            )}
          </div>
          {isAdmin && (
            <Field label="Supplier">
              <select className="luxe-input w-full px-4 py-2.5 text-sm" value={form.supplier_id || ""} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                <option value="">— None —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Product Type" testid="form-product-type">
            <select
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.product_type || ""}
              onChange={(e) => setForm({ ...form, product_type: e.target.value, product_type_custom: e.target.value === "custom" ? form.product_type_custom : "" })}
              data-testid="product-type-input"
            >
              {PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {form.product_type === "custom" && (
              <input
                className="luxe-input w-full px-4 py-2.5 text-sm mt-2"
                value={form.product_type_custom || ""}
                onChange={(e) => setForm({ ...form, product_type_custom: e.target.value })}
                placeholder="e.g. Anklet, Brooch…"
                data-testid="product-type-custom-input"
              />
            )}
          </Field>
          <Field label="Metal" testid="form-metal">
            <select
              className="luxe-input w-full px-4 py-2.5 text-sm"
              value={form.metal || ""}
              onChange={(e) => setForm({ ...form, metal: e.target.value, metal_custom: e.target.value === "custom" ? form.metal_custom : "" })}
              data-testid="metal-input"
            >
              {METALS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {form.metal === "custom" && (
              <input
                className="luxe-input w-full px-4 py-2.5 text-sm mt-2"
                value={form.metal_custom || ""}
                onChange={(e) => setForm({ ...form, metal_custom: e.target.value })}
                placeholder="e.g. Platinum, Rose Gold 18K…"
                data-testid="metal-custom-input"
              />
            )}
          </Field>
          <div className="md:col-span-2">
            <Field label="Description">
              <textarea className="luxe-input w-full px-4 py-3 text-sm min-h-[80px]" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
          </div>

          {/* Photos */}
          <div className="md:col-span-2">
            <div className="overline mb-2">Photos</div>
            <div className="flex items-center gap-3 flex-wrap">
              {(form.photos || []).map((p, i) => (
                <div key={p || `photo-${i}`} className="relative w-20 h-20 border border-white/10 bg-black/30 flex items-center justify-center text-[#D4AF37] mono text-[0.65rem]">
                  IMG {i + 1}
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-black border border-white/20 flex items-center justify-center text-[#8A8A8A] hover:text-red-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 border border-dashed border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] cursor-pointer hover:bg-[#D4AF37]/5 transition-colors" data-testid="upload-photo">
                <Upload className="w-4 h-4" strokeWidth={1.25} />
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
              </label>
            </div>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-outline-gold px-6 py-3">Cancel</button>
            <button type="submit" data-testid="submit-product" className="btn-gold px-6 py-3">{editing ? "Save Changes" : "Add Piece"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, testid, hint }) {
  return (
    <div data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      {children}
      {hint && <div className="text-[0.65rem] text-[#8A8A8A] mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}

function StockMoveModal({ product, onClose, onSubmit, isAdmin = false }) {
  const [type, setType] = useState("IN");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  // Stock movement operates on the inventory `stock` field (not the per-piece
  // weight `quantity`). Falls back to legacy quantity for un-migrated rows.
  const currentStock = product.stock != null ? parseFloat(product.stock) : parseFloat(product.quantity);
  const stockUnit = product.stock_unit || product.unit;
  const stockCustomUnit = product.stock_custom_unit || product.custom_unit;
  const qtyNum = parseFloat(quantity);
  // SET mode = overwrite; IN/OUT = delta. Preview shows the resulting stock.
  const remainingAfter = !isNaN(qtyNum)
    ? (type === "SET" ? qtyNum : currentStock + (type === "IN" ? 1 : -1) * qtyNum)
    : null;
  const invalid =
    !quantity ||
    isNaN(qtyNum) ||
    qtyNum < 0 ||
    (type !== "SET" && qtyNum <= 0) ||
    (remainingAfter !== null && remainingAfter < 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-md fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Stock Movement</div>
            <h3 className="serif text-xl mt-1">{product.name}</h3>
            <div className="mono text-[0.65rem] text-[#666] mt-0.5">
              {product.sku} · Current: {formatQty(currentStock, stockUnit, stockCustomUnit)}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <div className="px-7 py-6 space-y-4">
          <div>
            <div className="overline mb-2">Direction</div>
            <div className={`grid ${isAdmin ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
              <button
                type="button"
                onClick={() => setType("IN")}
                data-testid="movement-in"
                className={`py-3 text-xs uppercase tracking-wider border transition-all ${type === "IN" ? "border-[#7BA585] text-[#7BA585] bg-[#7BA585]/10" : "border-white/10 text-[#8A8A8A]"}`}
              >
                Stock In
              </button>
              <button
                type="button"
                onClick={() => setType("OUT")}
                data-testid="movement-out"
                className={`py-3 text-xs uppercase tracking-wider border transition-all ${type === "OUT" ? "border-[#C99356] text-[#C99356] bg-[#C99356]/10" : "border-white/10 text-[#8A8A8A]"}`}
              >
                Stock Out
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => { setType("SET"); setQuantity(currentStock); }}
                  data-testid="movement-set"
                  className={`py-3 text-xs uppercase tracking-wider border transition-all ${type === "SET" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#8A8A8A]"}`}
                  title="Admin only — directly overwrite stock (use for stocktake corrections / wrong-click recovery)"
                >
                  Set To…
                </button>
              )}
            </div>
            {type === "SET" && (
              <div className="text-[0.65rem] text-[#D4AF37] mt-2 leading-snug">
                ⚠️ Overwrites stock to the exact value below. Use when a piece was wrongly marked sold-out or for stocktake corrections.
              </div>
            )}
          </div>
          <div>
            <div className="overline mb-2">
              {type === "SET" ? `New stock value (${unitLabel(stockUnit, stockCustomUnit)})` : `Quantity (${unitLabel(stockUnit, stockCustomUnit)})`}
            </div>
            <input
              type="number" step="0.01" min="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="movement-qty"
            />
            {quantity && !isNaN(parseFloat(quantity)) && (
              <div className="mt-2 text-[0.7rem] mono" data-testid="movement-preview">
                <span className="text-[#666]">{type === "SET" ? "Stock will be set to: " : "After this movement: "}</span>
                <span className={remainingAfter < 0 ? "text-red-400" : "text-[#D4AF37]"}>
                  {formatQty(Math.max(0, remainingAfter), stockUnit, stockCustomUnit)}
                </span>
                {remainingAfter < 0 && (
                  <span className="ml-2 text-red-400">· Insufficient stock</span>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="overline mb-2">Notes</div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" placeholder="Optional" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button
              data-testid="submit-movement"
              onClick={() => onSubmit({ product_id: product.id, type, quantity: parseFloat(quantity), notes })}
              className="btn-gold px-5 py-2.5"
              disabled={invalid}
            >
              Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// BulkStockModal — admin-only CSV upload to SET stock on many pieces at once.
// CSV header: sku,stock,notes (notes optional). Dry-run preview by default so
// the admin can spot SKU typos before committing.
// ─────────────────────────────────────────────────────────────────────────────
function BulkStockModal({ onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // dry-run result
  const [committed, setCommitted] = useState(null);  // final commit result
  const fileRef = useRef(null);

  const runUpload = async (dryRun) => {
    if (!file) return toast.error("Pick a CSV file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/products/stock-movements/bulk-set?dry_run=${dryRun ? "true" : "false"}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (dryRun) {
        setPreview(data);
        setCommitted(null);
      } else {
        setCommitted(data);
        toast.success(`Bulk SET: ${data.applied_count} updated, ${data.skipped_count} skipped`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    // Tiny CSV template — comments explain the format right inside Excel.
    const csv = [
      "sku,stock,notes",
      "OJ-00001,5,Stocktake Feb 2026",
      "OJ-00002,0,Lost piece - write off",
      "OJ-00003,12,Found in safe deposit",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-stock-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCurrent = async () => {
    setBusy(true);
    try {
      // axios `responseType: 'blob'` so the CSV body isn't parsed as JSON.
      const res = await api.get("/products/stock-movements/export-stock", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Use server-suggested filename if present.
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : "opikjewels-stock.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Current stock exported. Edit in Excel then re-upload.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const result = committed || preview;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="bulk-stock-modal">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-2xl fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline text-[#D4AF37]">Admin · Bulk Stock SET</div>
            <h3 className="serif text-xl mt-1">Update stock from CSV</h3>
            <div className="text-[0.65rem] text-[#8A8A8A] mt-1">
              Use for monthly stocktake or batch corrections. Each row overwrites the stock value (does not add/subtract).
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-stock-close"><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>

        <div className="px-7 py-6 space-y-5">
          {/* Stocktake workflow card */}
          <div className="bg-white/[0.02] border border-white/5 p-4">
            <div className="text-[0.65rem] uppercase tracking-wider text-[#D4AF37] mb-2">
              Monthly stocktake workflow
            </div>
            <div className="text-[0.7rem] text-[#8A8A8A] mb-3 leading-snug">
              1. Export current stock → 2. Edit values in Excel after physical count → 3. Re-upload to commit.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={exportCurrent}
                disabled={busy}
                className="btn-outline-gold py-2.5 text-[0.65rem] uppercase tracking-wider disabled:opacity-50"
                data-testid="bulk-stock-export"
                title="Download current stock for every Available piece"
              >
                ↓ Export current stock
              </button>
              <button
                type="button"
                onClick={downloadTemplate}
                className="border border-white/10 hover:border-[#D4AF37]/40 text-[#8A8A8A] hover:text-[#D4AF37] py-2.5 text-[0.65rem] uppercase tracking-wider transition-colors"
                data-testid="bulk-stock-template"
                title="Blank 3-row template if you just want to update a few specific SKUs"
              >
                ↓ Blank template
              </button>
            </div>
            <div className="text-[0.6rem] text-[#666] mt-2 mono">
              CSV columns: sku, stock, notes
            </div>
          </div>

          {/* File picker */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setCommitted(null); }}
              className="hidden"
              data-testid="bulk-stock-file"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-white/15 hover:border-[#D4AF37]/40 py-8 text-center transition-colors"
              data-testid="bulk-stock-picker"
            >
              <FileUp className="w-6 h-6 mx-auto text-[#8A8A8A]" strokeWidth={1.25} />
              <div className="text-sm mt-2 text-[#EAEAEA]">{file ? file.name : "Click to pick a .csv file"}</div>
              {file && <div className="text-[0.65rem] text-[#8A8A8A] mt-1">{(file.size / 1024).toFixed(1)} KB</div>}
            </button>
          </div>

          {/* Action buttons */}
          {file && !committed && (
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => runUpload(true)}
                className="btn-outline-gold py-3 text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="bulk-stock-preview"
              >
                {busy ? "Reading…" : "1) Preview (dry-run)"}
              </button>
              <button
                type="button"
                disabled={busy || !preview}
                onClick={() => runUpload(false)}
                className="btn-gold py-3 text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="bulk-stock-commit"
                title={!preview ? "Preview first to confirm" : "Apply the CSV"}
              >
                {busy ? "Applying…" : "2) Commit changes"}
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="border border-white/5 bg-[#0A0A0A] divide-y divide-white/5">
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="text-sm">
                  {committed ? "✅ Applied" : "👀 Preview only — not saved"}
                </div>
                <div className="text-[0.7rem] text-[#8A8A8A]">
                  <span className="text-[#7BA585]">{result.applied_count} updated</span>
                  {" · "}
                  <span className="text-[#C99356]">{result.skipped_count} skipped</span>
                </div>
              </div>
              {result.applied.length > 0 && (
                <div className="max-h-48 overflow-y-auto">
                  {result.applied.slice(0, 50).map((r) => (
                    <div key={`a-${r.row}`} className="px-4 py-2 flex items-center justify-between text-[0.7rem] hover:bg-white/[0.02]">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-3 h-3 text-[#7BA585] shrink-0" strokeWidth={1.5} />
                        <span className="mono text-[#D4AF37]">{r.sku}</span>
                        <span className="text-[#8A8A8A] truncate">· {r.product_name}</span>
                      </div>
                      <div className="mono shrink-0">
                        <span className="text-[#666]">{r.old_stock}</span>
                        <span className="text-[#666] mx-1">→</span>
                        <span className="text-[#EAEAEA]">{r.new_stock}</span>
                      </div>
                    </div>
                  ))}
                  {result.applied.length > 50 && (
                    <div className="px-4 py-2 text-[0.65rem] text-[#666] italic">
                      …and {result.applied.length - 50} more rows
                    </div>
                  )}
                </div>
              )}
              {result.skipped.length > 0 && (
                <div className="max-h-32 overflow-y-auto">
                  {result.skipped.map((r) => (
                    <div key={`s-${r.row}`} className="px-4 py-2 flex items-center justify-between text-[0.7rem] hover:bg-white/[0.02]">
                      <div className="flex items-center gap-2 min-w-0">
                        <AlertTriangle className="w-3 h-3 text-[#C99356] shrink-0" strokeWidth={1.5} />
                        <span className="text-[#8A8A8A]">Row {r.row}:</span>
                        <span className="mono">{r.sku || "(blank)"}</span>
                      </div>
                      <div className="text-[0.7rem] text-[#C99356] shrink-0">{r.reason}</div>
                    </div>
                  ))}
                </div>
              )}
              {committed && (
                <div className="px-4 py-3 flex justify-end">
                  <button
                    type="button"
                    onClick={onApplied}
                    className="btn-gold px-5 py-2 text-xs uppercase tracking-wider"
                    data-testid="bulk-stock-done"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function CategorySettingsModal({ onClose, onSaved }) {
  const [keywords, setKeywords] = useState("");
  const [skipWords, setSkipWords] = useState("");
  const [defaults, setDefaults] = useState({ keywords: [], skip_words: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/category-settings").then(({ data }) => {
      setKeywords((data.keywords || []).join(", "));
      setSkipWords((data.skip_words || []).join(", "));
      setDefaults({ keywords: data.default_keywords || [], skip_words: data.default_skip_words || [] });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put("/products/category-settings", {
        keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        skip_words: skipWords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success("Category rules updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const resetToDefaults = () => {
    setKeywords(defaults.keywords.join(", "));
    setSkipWords(defaults.skip_words.join(", "));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-2xl fade-up" data-testid="category-settings-modal">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Inventory</div>
            <h3 className="serif text-2xl mt-1">Auto-Category Rules</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        {loading ? (
          <div className="px-7 py-10 text-center text-[#8A8A8A] overline">Loading…</div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-5">
            <div>
              <div className="overline mb-2">Gemstone Keywords</div>
              <textarea
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                rows={4}
                className="luxe-input w-full px-4 py-2.5 text-sm font-mono"
                data-testid="kw-input"
              />
              <div className="mt-1.5 text-[0.65rem] text-[#666]">
                Comma-separated. When any of these words is found in a product name (any position), it becomes the category.
              </div>
            </div>
            <div>
              <div className="overline mb-2">Skip Words (color / qualifier modifiers)</div>
              <textarea
                value={skipWords}
                onChange={(e) => setSkipWords(e.target.value)}
                rows={4}
                className="luxe-input w-full px-4 py-2.5 text-sm font-mono"
                data-testid="skip-input"
              />
              <div className="mt-1.5 text-[0.65rem] text-[#666]">
                These words are ignored when no keyword matches and we have to pick a fallback (e.g. "Blue Sapphire" skips "blue").
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-white/5">
              <button
                type="button"
                onClick={resetToDefaults}
                className="text-[0.62rem] uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37]"
                data-testid="reset-defaults"
              >
                Reset to defaults
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
                <button type="submit" data-testid="save-category-settings" className="btn-gold px-5 py-2.5">Save Rules</button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function TransferModal({ product, onClose, onSaved }) {
  const currentWh = product.warehouse || "ONLINE_SALE";
  // With 3 warehouses the operator must pick the destination. Default to
  // whichever isn't the current one alphabetically so a single-click
  // "same-day transfer" flow still feels quick.
  const otherWarehouses = WAREHOUSES.filter((w) => w.value !== currentWh);
  const [targetWh, setTargetWh] = useState(otherWarehouses[0]?.value || "SHOWROOM");
  // Transfers move the inventory `stock` field with its own unit (admin-only).
  const currentStock = product.stock != null ? parseFloat(product.stock) : parseFloat(product.quantity);
  const stockUnit = product.stock_unit || product.unit;
  const stockCustomUnit = product.stock_custom_unit || product.custom_unit;
  const [quantity, setQty] = useState(currentStock);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/products/${product.id}/transfers`).then(({ data }) => setHistory(data)).catch(() => {});
  }, [product.id]);

  const isPartial = parseFloat(quantity) > 0 && parseFloat(quantity) < currentStock;
  const tooMuch = parseFloat(quantity) > currentStock + 1e-9;

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("Reason is required");
    if (tooMuch) return toast.error("Cannot transfer more than available stock");
    setSubmitting(true);
    try {
      await api.post("/products/transfers", {
        product_id: product.id,
        to_warehouse: targetWh,
        quantity: parseFloat(quantity),
        reason: reason.trim(),
      });
      toast.success(`Transferred ${quantity} ${unitLabel(stockUnit, stockCustomUnit)} to ${WAREHOUSE_LABEL[targetWh]}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto fade-up" data-testid="transfer-modal">
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Stock Transfer</div>
            <h3 className="serif text-2xl mt-1">{product.name}</h3>
            <div className="mono text-[0.65rem] text-[#666] mt-1">{product.sku} · Available: {formatQty(currentStock, stockUnit, stockCustomUnit)}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-5" data-testid="transfer-form">
          {/* Warehouse direction visual */}
          <div className="flex items-center gap-3 p-4 border border-white/10 bg-white/[0.02]">
            <div className="flex-1 text-center">
              <div className="overline mb-1">From</div>
              <div className="flex items-center justify-center gap-2 text-[#D4AF37]">
                {currentWh === "SHOWROOM" ? <Store className="w-4 h-4" /> : currentWh === "LIVE_PRODUCT" ? <Video className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                <span className="serif text-lg">{WAREHOUSE_LABEL[currentWh]}</span>
              </div>
            </div>
            <ArrowLeftRight className="w-5 h-5 text-[#7BA585]" />
            <div className="flex-1 text-center">
              <div className="overline mb-1">To</div>
              <select
                value={targetWh}
                onChange={(e) => setTargetWh(e.target.value)}
                className="luxe-input px-3 py-1.5 text-sm text-center text-[#7BA585]"
                data-testid="transfer-target-warehouse"
              >
                {otherWarehouses.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
          </div>

          <Field label={`Quantity to transfer (${unitLabel(stockUnit, stockCustomUnit)})`}>
            <input
              required
              type="number"
              step="0.01"
              min="0.01"
              max={currentStock}
              value={quantity}
              onChange={(e) => setQty(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="transfer-qty"
            />
            <div className="mt-1.5 text-[0.65rem] mono text-[#666]">
              {isPartial ? (
                <span className="text-[#C99356]">Partial transfer — source piece stays at {WAREHOUSE_LABEL[currentWh]} with reduced qty; a new SKU is created at {WAREHOUSE_LABEL[targetWh]} for the moved portion.</span>
              ) : (
                <span>Full transfer — the piece moves entirely to {WAREHOUSE_LABEL[targetWh]}.</span>
              )}
            </div>
          </Field>

          <Field label="Reason (required)">
            <textarea
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Customer requested viewing in showroom · or · Returning to online inventory after no sale"
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="transfer-reason"
            />
          </Field>

          {history.length > 0 && (
            <div data-testid="transfer-history">
              <div className="overline mb-2">Transfer History ({history.length})</div>
              <div className="max-h-40 overflow-y-auto space-y-1.5">
                {history.slice(0, 10).map((h) => (
                  <div key={h.id} className="flex items-center justify-between gap-3 text-[0.7rem] py-1.5 border-b border-white/5 last:border-0">
                    <div className="text-[#8A8A8A] mono">{(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    <div className="flex-1 text-[#EAEAEA]">
                      {WAREHOUSE_LABEL[h.from_warehouse]} → {WAREHOUSE_LABEL[h.to_warehouse]} · {formatQty(h.quantity, product.unit, product.custom_unit)}
                    </div>
                    <div className="text-[#666] mono text-right max-w-[140px] truncate" title={`${h.staff_name} — ${h.reason}`}>
                      {h.staff_name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button
              type="submit"
              data-testid="submit-transfer"
              disabled={submitting || tooMuch || !parseFloat(quantity)}
              className="btn-gold px-5 py-2.5 inline-flex items-center gap-2"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              {submitting ? "Transferring…" : `Transfer to ${WAREHOUSE_LABEL[targetWh]}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



/* ===================================================================== */
/* QR Code modal — single source of truth for QR generate / regenerate / */
/* download. Keeps the inventory row UI uncluttered (one icon → opens   */
/* this modal).                                                          */
/* ===================================================================== */
function QRModal({ product, onClose, onChange }) {
  const [state, setState] = useState({
    enabled: !!product.qr_enabled,
    short_id: product.qr_short_id || "",
    url: "",
  });
  const [busy, setBusy] = useState(false);
  const [pngBlobUrl, setPngBlobUrl] = useState("");

  // Auth-bearing fetch of QR PNG → blob URL (browser <img> can't send Bearer).
  useEffect(() => {
    let cancelled = false;
    let urlToRevoke = "";
    (async () => {
      if (!state.enabled || !state.short_id) {
        setPngBlobUrl("");
        return;
      }
      try {
        const res = await fetch(`${API}/products/${product.id}/qr.png`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("gv_token") || ""}` },
        });
        if (!res.ok) throw new Error("preview-failed");
        const blob = await res.blob();
        urlToRevoke = URL.createObjectURL(blob);
        if (!cancelled) setPngBlobUrl(urlToRevoke);
      } catch {
        if (!cancelled) setPngBlobUrl("");
      }
    })();
    return () => {
      cancelled = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [state.enabled, state.short_id, product.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/qr`);
      setState({ enabled: true, short_id: data.short_id, url: data.url });
      toast.success("QR generated");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate QR");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate? Old printed labels will stop working.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/qr/regenerate`);
      setState({ enabled: true, short_id: data.short_id, url: data.url });
      toast.success("QR regenerated — reprint your label");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to regenerate QR");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove QR? Existing labels for this piece will stop working.")) return;
    setBusy(true);
    try {
      await api.delete(`/products/${product.id}/qr`);
      setState({ enabled: false, short_id: "", url: "" });
      toast.success("QR disabled");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove QR");
    } finally {
      setBusy(false);
    }
  };

  const downloadLabel = (kind) => {
    const filename = kind === "sheet"
      ? `labels-A4-${product.sku}.pdf`
      : `label-${product.sku}.pdf`;
    const path = kind === "sheet"
      ? `/products/${product.id}/qr-sheet.pdf`
      : `/products/${product.id}/qr-label.pdf`;
    downloadAuthedFile(path, filename).catch((err) => toast.error(err.message || "Download failed"));
  };

  // ---- Hangtag tab state (showroom printable price tags) ----
  const [tab, setTab] = useState("qr");  // "qr" | "hangtags"
  const DEFAULT_FIELDS = ["name", "weight", "treatment", "origin", "cert", "price"];
  const [hangtag, setHangtag] = useState({
    size: "small",
    qty: 0,
    singleTag: false,
    fields: DEFAULT_FIELDS,  // ordered: top → bottom on the tag
  });
  const [hangtagFields, setHangtagFields] = useState([]);  // [{id, label}, ...] from backend

  useEffect(() => {
    let mounted = true;
    api.get("/products/_hangtag/fields").then(({ data }) => {
      if (mounted) setHangtagFields(data || []);
    }).catch(() => { /* non-critical */ });
    return () => { mounted = false; };
  }, []);

  // Toggle a field on/off — preserves order of insertion (so the user sees
  // their selections render top-to-bottom on the tag in the order they
  // picked).
  const toggleField = (fid) => {
    setHangtag((h) => {
      const present = h.fields.includes(fid);
      return {
        ...h,
        fields: present ? h.fields.filter((f) => f !== fid) : [...h.fields, fid],
      };
    });
  };

  const HANGTAG_SIZES = [
    { id: "small", label: "Hangtag", dims: "25 × 54 mm", per: "12 / sheet", note: "For ring/chain loop" },
    { id: "tiny", label: "Price Sticker", dims: "30 × 20 mm", per: "78 / sheet", note: "Generic small sticker" },
    { id: "medium", label: "Tray Label", dims: "50 × 30 mm", per: "24 / sheet", note: "More detail visible" },
  ];

  const buildHangtagPath = () => {
    const params = new URLSearchParams({
      size: hangtag.size,
      single_tag: hangtag.singleTag ? "true" : "false",
    });
    if (hangtag.fields.length) params.append("fields", hangtag.fields.join(","));
    if (hangtag.qty > 0) params.append("qty", String(hangtag.qty));
    return `/products/${product.id}/hangtag.pdf?${params.toString()}`;
  };

  const downloadHangtag = () => {
    const fname = `hangtag-${hangtag.size}-${hangtag.singleTag ? "label" : "sheet"}-${product.sku}.pdf`;
    downloadAuthedFile(buildHangtagPath(), fname).catch((err) => toast.error(err.message || "Download failed"));
  };

  // Open the PDF inline in a new tab → mobile Safari/Chrome shows the
  // system "Share → Print" sheet which can route to a Bluetooth label
  // printer (Phomemo / NIIMBOT) or any AirPrint/Cloud printer.
  const printHangtag = async () => {
    try {
      const blob = await api.get(buildHangtagPath(), { responseType: "blob" });
      const url = URL.createObjectURL(blob.data);
      const w = window.open(url, "_blank", "noopener");
      // After load, trigger the print dialog automatically on desktop.
      // Mobile browsers ignore w.print() but the user can use Share→Print.
      if (w) {
        w.addEventListener("load", () => {
          // Some mobile browsers throw on programmatic print — the user can still
          // trigger Share→Print manually, so we just log instead of erroring out.
          try { w.print(); } catch (e) { console.warn("Auto-print blocked:", e); }
        }, { once: true });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open print dialog");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-up" data-testid="qr-modal">
        <button
          onClick={onClose}
          className="absolute right-5 top-5 text-[#8A8A8A] hover:text-[#EAEAEA] z-10"
          aria-label="Close"
          data-testid="qr-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-7 py-6 border-b border-white/5">
          <div className="overline mb-1">QR Reference</div>
          <h2 className="serif text-2xl">{product.name}</h2>
          <div className="text-[0.7rem] mono text-[#8A8A8A] mt-1">{product.sku}</div>
        </div>

        <div className="px-7 py-6 space-y-5">
          {!state.enabled ? (
            <div className="border border-dashed border-white/15 px-5 py-8 text-center space-y-4" data-testid="qr-empty-state">
              <QrCode className="w-10 h-10 mx-auto text-[#D4AF37]/40" strokeWidth={1} />
              <div>
                <div className="text-sm text-[#EAEAEA]">No QR yet for this piece.</div>
                <div className="text-xs text-[#8A8A8A] mt-1">
                  Generate a unique short reference and print a label to stick on the piece for staff identification and stock-takes.
                </div>
              </div>
              <button
                onClick={generate}
                disabled={busy}
                data-testid="qr-generate-btn"
                className="btn-gold px-6 py-3 inline-flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" strokeWidth={1.5} />
                {busy ? "Generating…" : "Generate QR"}
              </button>
            </div>
          ) : (
            <>
              {/* PNG preview + identifier */}
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-5 items-start">
                <div className="sm:col-span-2 bg-white p-3 border border-white/10 flex items-center justify-center">
                  {pngBlobUrl ? (
                    <img
                      src={pngBlobUrl}
                      alt="QR preview"
                      className="w-full h-auto max-w-[220px]"
                      data-testid="qr-preview-img"
                    />
                  ) : (
                    <div className="aspect-square w-full bg-[#f5f5f5] flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-[#cccccc]" strokeWidth={1} />
                    </div>
                  )}
                </div>
                <div className="sm:col-span-3 space-y-3">
                  <div>
                    <div className="overline mb-1">Short Reference</div>
                    <div className="mono text-lg text-[#D4AF37]" data-testid="qr-short-id">{state.short_id}</div>
                    <div className="text-[0.6rem] mono text-[#666] mt-1">Print on the label for fast staff scanning &amp; stock-takes.</div>
                  </div>
                </div>
              </div>

              {/* Tabs: QR Labels (default) | Showroom Hangtags */}
              <div className="border-t border-white/5 pt-5">
                <div className="flex gap-1 mb-5 border-b border-white/5">
                  <button
                    onClick={() => setTab("qr")}
                    data-testid="tab-qr"
                    className={`px-4 py-2 text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                      tab === "qr" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#8A8A8A] hover:text-[#EAEAEA]"
                    }`}
                  >
                    QR Labels
                  </button>
                  <button
                    onClick={() => setTab("hangtags")}
                    data-testid="tab-hangtags"
                    className={`px-4 py-2 text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                      tab === "hangtags" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#8A8A8A] hover:text-[#EAEAEA]"
                    }`}
                  >
                    Showroom Hangtags
                  </button>
                </div>

                {tab === "qr" && (
                  <>
                    <div className="overline mb-3">Print</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => downloadLabel("single")}
                        disabled={busy}
                        data-testid="qr-download-label"
                        className="btn-gold px-5 py-3 inline-flex items-center justify-center gap-2 text-sm"
                      >
                        <Printer className="w-4 h-4" strokeWidth={1.5} />
                        Single Label (50×30 mm)
                      </button>
                      <button
                        onClick={() => downloadLabel("sheet")}
                        disabled={busy}
                        data-testid="qr-download-sheet"
                        className="btn-outline-gold px-5 py-3 inline-flex items-center justify-center gap-2 text-sm"
                      >
                        <Printer className="w-4 h-4" strokeWidth={1.5} />
                        A4 Sheet (24 labels)
                      </button>
                    </div>
                    <div className="text-[0.65rem] text-[#666] mt-3 leading-relaxed">
                      Single label = Brother / Dymo small label stock. A4 sheet = standard office printer, cut along the dashed lines.
                    </div>
                  </>
                )}

                {tab === "hangtags" && (
                  <div className="space-y-5" data-testid="hangtag-panel">
                    <div>
                      <div className="overline mb-3">Label Size</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {HANGTAG_SIZES.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setHangtag({ ...hangtag, size: s.id })}
                            data-testid={`hangtag-size-${s.id}`}
                            className={`text-left p-3 border transition-colors min-h-[44px] ${
                              hangtag.size === s.id
                                ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]"
                                : "border-white/10 hover:border-white/25"
                            }`}
                          >
                            <div className="text-xs text-[#EAEAEA]">{s.label}</div>
                            <div className="mono text-[0.65rem] text-[#D4AF37] mt-1">{s.dims}</div>
                            <div className="mono text-[0.6rem] text-[#666] mt-0.5">{s.per}</div>
                            <div className="text-[0.6rem] text-[#8A8A8A] mt-1 italic">{s.note}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-end gap-4">
                      <div>
                        <label className="overline block mb-1">Quantity</label>
                        <input
                          type="number"
                          min="0"
                          max="500"
                          value={hangtag.qty}
                          onChange={(e) => setHangtag({ ...hangtag, qty: Number(e.target.value) })}
                          placeholder={hangtag.singleTag ? "1 label" : "Fill sheet"}
                          className="luxe-input w-32 px-3 py-2 text-sm mono"
                          data-testid="hangtag-qty"
                        />
                      </div>
                    </div>

                    {/* Field picker — choose what data lines appear on the tag,
                        in the order the user picks. Brand mark, QR, and SKU
                        are structural and always rendered. */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="overline">Fields on Tag</div>
                        <div className="flex gap-2 text-[0.6rem] uppercase tracking-wider">
                          <button
                            onClick={() => setHangtag({ ...hangtag, fields: hangtagFields.map((f) => f.id) })}
                            data-testid="hangtag-fields-all"
                            className="text-[#D4AF37] hover:underline"
                          >Select All</button>
                          <span className="text-[#666]">·</span>
                          <button
                            onClick={() => setHangtag({ ...hangtag, fields: DEFAULT_FIELDS })}
                            data-testid="hangtag-fields-default"
                            className="text-[#8A8A8A] hover:underline"
                          >Default</button>
                          <span className="text-[#666]">·</span>
                          <button
                            onClick={() => setHangtag({ ...hangtag, fields: [] })}
                            data-testid="hangtag-fields-none"
                            className="text-[#8A8A8A] hover:underline"
                          >None</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {hangtagFields.map((f) => {
                          const idx = hangtag.fields.indexOf(f.id);
                          const checked = idx >= 0;
                          // Hint at the resolved value from the product so the
                          // user knows which fields will actually appear.
                          const previewMap = {
                            name: product.name,
                            weight: (product.carat || product.quantity) ? `${product.carat || product.quantity} ${product.unit || "ct"}` : "",
                            dimension: product.dimension,
                            size: product.size || product.dimension,
                            origin: product.origin,
                            treatment: product.treatment,
                            cert: [product.certification_lab, product.cert_number].filter(Boolean).join(" "),
                            color: product.color,
                            shape: product.shape,
                            clarity: product.clarity,
                            price: product.selling_price ? `Rp ${Number(product.selling_price).toLocaleString("id-ID")}` : "",
                          };
                          const preview = previewMap[f.id] || "";
                          const blank = !preview;
                          return (
                            <button
                              key={f.id}
                              onClick={() => toggleField(f.id)}
                              data-testid={`hangtag-field-${f.id}`}
                              className={`text-left p-2 border min-h-[44px] transition-colors ${
                                checked
                                  ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]"
                                  : "border-white/10 hover:border-white/25"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-[0.7rem] text-[#EAEAEA]">{f.label}</div>
                                {checked && idx >= 0 && (
                                  <div className="mono text-[0.55rem] text-[#D4AF37]">#{idx + 1}</div>
                                )}
                              </div>
                              <div className={`text-[0.6rem] mono truncate ${blank ? "text-[#555] italic" : "text-[#8A8A8A]"}`}>
                                {blank ? "(blank — will be skipped)" : preview}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="text-[0.6rem] text-[#666] mt-2">
                        Order = top-to-bottom on the tag. Numbers on each pill show position. Blank fields auto-skip.
                      </div>
                    </div>

                    {/* Output mode — Sheet (A4, cut by hand) vs Single Label
                        (one tag per page, page-size = tag-size, for label-roll
                        printers like Brother / Dymo / Phomemo / NIIMBOT) */}
                    <div>
                      <div className="overline mb-2">Output Mode</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          onClick={() => setHangtag({ ...hangtag, singleTag: false })}
                          data-testid="hangtag-mode-sheet"
                          className={`text-left p-3 border transition-colors min-h-[44px] ${
                            !hangtag.singleTag
                              ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]"
                              : "border-white/10 hover:border-white/25"
                          }`}
                        >
                          <div className="text-xs text-[#EAEAEA]">A4 Sheet · multi-up</div>
                          <div className="text-[0.65rem] text-[#8A8A8A] mt-1 leading-relaxed">
                            Standard A4 with dashed cut-guides. For office printers.
                          </div>
                        </button>
                        <button
                          onClick={() => setHangtag({ ...hangtag, singleTag: true })}
                          data-testid="hangtag-mode-single"
                          className={`text-left p-3 border transition-colors min-h-[44px] ${
                            hangtag.singleTag
                              ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]"
                              : "border-white/10 hover:border-white/25"
                          }`}
                        >
                          <div className="text-xs text-[#EAEAEA]">Label Roll · one-per-page</div>
                          <div className="text-[0.65rem] text-[#8A8A8A] mt-1 leading-relaxed">
                            Page-size = tag-size. For Brother, Dymo, Phomemo, NIIMBOT Bluetooth printers.
                          </div>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        onClick={printHangtag}
                        disabled={busy}
                        data-testid="hangtag-print"
                        className="btn-gold w-full px-5 py-3 inline-flex items-center justify-center gap-2 text-sm"
                      >
                        <Printer className="w-4 h-4" strokeWidth={1.5} />
                        Print Now
                      </button>
                      <button
                        onClick={downloadHangtag}
                        disabled={busy}
                        data-testid="hangtag-download"
                        className="btn-outline-gold w-full px-5 py-3 inline-flex items-center justify-center gap-2 text-sm"
                      >
                        Download PDF
                      </button>
                    </div>
                    <div className="text-[0.65rem] text-[#666] leading-relaxed space-y-1">
                      <div>• <span className="text-[#EAEAEA]">Print Now</span> opens the system print dialog — pick your connected printer (USB, AirPrint, or Bluetooth from your phone).</div>
                      <div>• On <span className="text-[#EAEAEA]">iPhone</span>, after the PDF opens use <span className="text-[#D4AF37]">Share → Print</span> to route to a paired Phomemo / NIIMBOT.</div>
                      <div>• Blank fields are skipped automatically. Add treatment / origin / cert# / price on the product to populate future prints.</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lifecycle actions */}
              <div className="border-t border-white/5 pt-5 flex flex-wrap gap-3">
                <button
                  onClick={regenerate}
                  disabled={busy}
                  data-testid="qr-regenerate"
                  className="px-4 py-2.5 text-xs uppercase tracking-wider border border-[#C99356]/40 text-[#C99356] hover:bg-[#C99356]/10 inline-flex items-center gap-2"
                  title="Mint new short reference — old printed labels stop working"
                >
                  <RotateCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Regenerate
                </button>
                <button
                  onClick={remove}
                  disabled={busy}
                  data-testid="qr-remove"
                  className="px-4 py-2.5 text-xs uppercase tracking-wider border border-red-500/30 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-2"
                  title="Disable QR — existing labels become unscannable"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Remove QR
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
